// ========================================
// Justificantes View - Medical Certificates
// Quien revisa (Admin, RRHH, módulo Justificantes) trabaja en una bandeja: a la izquierda los
// justificantes (los sin abrir arriba), a la derecha el elegido con su vista previa; abrirlo lo
// deja como visto. Profesores y alumnos suben los suyos desde la propia página y ven su estado.
// ========================================

const JustificantesView = {
    group: 'todos', // 'todos' | 'profesor' | 'alumno' — solo para quien revisa
    search: '',
    onlyNew: false,
    selectedId: null,
    deleting: new Set(),

    isReviewer() {
        return AuthManager.isRRHH() || AuthManager.isAdmin() || AuthManager.hasModule('justificantes');
    },
    canUpload() {
        return AuthManager.isProfessor() || AuthManager.isStudent() || AuthManager.canManageModule('justificantes',['admin','rrhh']);
    },
    // Quien revisa borra cualquiera solo con Gestionar; quien sube borra los suyos (el servidor lo vuelve a comprobar).
    canDelete(doc) {
        const user = AuthManager.currentUser;
        if (this.isReviewer()) return AuthManager.canManageModule('justificantes',['admin','rrhh']);
        return !!user && (doc.studentId ? doc.studentId === user.studentId : doc.professorId === user.professorId);
    },

    ICONS: {
        download: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v11"/><path d="m7.5 10.5 4.5 4.5 4.5-4.5"/><path d="M5 20h14"/></svg>',
        expand: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 4h6v6"/><path d="M20 4 11 13"/><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/></svg>',
        trash: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16"/><path d="M9 7V4h6v3"/><path d="M6 7l1 13h10l1-13"/></svg>',
        eye: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>',
        upload: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 16V5"/><path d="m7.5 9.5 4.5-4.5 4.5 4.5"/><path d="M5 19h14"/></svg>',
        file: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="m9 14 2 2 4-4"/></svg>',
        send: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12 20 4l-6 16-3-7z"/><path d="m11 13 9-9"/></svg>'
    },

    all() {
        return DataManager.getAll(DataManager.KEYS.JUSTIFICANTES) || [];
    },
    isPdf(doc) { return String(doc.type || '').includes('pdf'); },
    ownerName(doc) { return doc.studentName || doc.professorName || 'Sin nombre'; },
    initials(name) { return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase(); },
    day(value) { return new Date(value).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }); },
    when(value) {
        const date = new Date(value), today = new Date();
        const days = Math.round((new Date(today.toDateString()) - new Date(date.toDateString())) / 86400000);
        if (days === 0) return 'hoy';
        if (days === 1) return 'ayer';
        return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    },

    render() {
        const currentUser = AuthManager.currentUser;
        const isReviewer = this.isReviewer();
        const isUploader = !!(currentUser && (currentUser.professorId || currentUser.studentId) && this.canUpload());
        if (!currentUser || !(isReviewer || isUploader)) {
            return `
                <div class="view-container dnr-proposal jv">
                    <div class="dnr-empty jv-empty">
                        <h3>Acceso restringido</h3>
                        <p>Esta sección es exclusiva para profesores, alumnos, Coordinación Académica, RRHH y Administración.</p>
                    </div>
                </div>`;
        }
        return `
            <div class="view-container dnr-proposal jv" id="justificantes-page">
                ${this.styles()}
                ${isReviewer ? this.renderReviewer() : this.renderUploader()}
            </div>
            <div id="preview-justificante-modal" class="modal-overlay jv-modal" style="display: none; opacity: 0;">
                <div class="dnr-proposal jv-modal-box">
                    <div class="jv-modal-head">
                        <h3 id="preview-title">Vista previa</h3>
                        <button type="button" class="jv-icon-btn" aria-label="Cerrar" ${UIEvents.attr("click", function(event,captured){JustificantesView.closePreview();}, [])}><i class="fas fa-times"></i></button>
                    </div>
                    <div id="preview-content" class="jv-modal-body"></div>
                </div>
            </div>`;
    },

    // ---------- Quien revisa: cifras, filtros, bandeja y el justificante elegido ----------

    renderReviewer() {
        return `
            <div class="jv-head">
                <span class="jv-mono">Personal y RRHH · Ausencias</span>
                <h1 class="jv-title">Justificantes</h1>
            </div>
            <div class="jv-stats" id="jv-stats">${this.renderStats()}</div>
            <section class="jv-card jv-toolbar" aria-label="Filtros">
                <label class="jv-search">
                    <i class="fas fa-search" aria-hidden="true"></i>
                    <input type="search" id="jv-search" placeholder="Buscar persona o archivo" aria-label="Buscar persona o archivo" value="${Utils.sanitizeHTML(this.search)}"
                        ${UIEvents.attr("input", function(event,captured){JustificantesView.search=this.value;JustificantesView.refreshInbox();}, [])}>
                </label>
                <div class="jv-segment" role="group" aria-label="Quién" id="jv-groups">${this.renderGroups()}</div>
                <label class="jv-check"><input type="checkbox" ${this.onlyNew ? 'checked' : ''} ${UIEvents.attr("change", function(event,captured){JustificantesView.onlyNew=this.checked;JustificantesView.refreshInbox();}, [])}> Solo sin abrir</label>
            </section>
            <div class="jv-layout">
                <section class="jv-card jv-inbox" aria-labelledby="jv-inbox-title">
                    <div class="jv-card-head">
                        <h2 class="jv-h2" id="jv-inbox-title">Bandeja</h2>
                        <span class="jv-hint">Lo sin abrir, arriba</span>
                    </div>
                    <ul class="jv-list" id="justificantes-grid"></ul>
                </section>
                <section class="jv-card jv-detail" id="jv-detail" aria-live="polite"></section>
            </div>`;
    },

    renderStats() {
        const all = this.all(), fresh = all.filter(d => !d.reviewedAt);
        const now = new Date(), month = (d, back) => { const m = new Date(now.getFullYear(), now.getMonth() - back, 1); const x = new Date(d.date); return x.getFullYear() === m.getFullYear() && x.getMonth() === m.getMonth(); };
        const monthName = back => new Date(now.getFullYear(), now.getMonth() - back, 1).toLocaleDateString('es-ES', { month: 'long' });
        const oldest = fresh.reduce((min, d) => !min || new Date(d.date) < new Date(min.date) ? d : min, null);
        const age = oldest ? Math.round((new Date(now.toDateString()) - new Date(new Date(oldest.date).toDateString())) / 86400000) : 0;
        const side = isStudent => {
            const docs = all.filter(d => !!d.studentId === isStudent);
            const people = new Set(docs.map(d => d.studentId || d.professorId)).size;
            const open = docs.filter(d => !d.reviewedAt).length;
            return [docs.length, people, open];
        };
        const [pDocs, pPeople, pNew] = side(false), [sDocs, sPeople, sNew] = side(true);
        const stat = (label, value, sub, extra = '') => `<div class="jv-stat ${extra}"><span class="jv-stat-label">${label}</span><span class="jv-stat-value">${value}</span><span class="jv-stat-sub">${sub}</span></div>`;
        return [
            stat('Sin abrir', fresh.length, fresh.length ? `El más antiguo, ${age === 0 ? 'de hoy' : age === 1 ? 'de ayer' : `hace ${age} días`}` : 'Todo visto', fresh.length ? 'is-warn' : ''),
            stat(`Recibidos en ${monthName(0)}`, all.filter(d => month(d, 0)).length, `${all.filter(d => month(d, 1)).length} en ${monthName(1)}`),
            stat('Profesorado', `${pDocs} <small>de ${pPeople} ${pPeople === 1 ? 'persona' : 'personas'}</small>`, pNew ? `${pNew} sin abrir` : 'Todo visto'),
            stat('Alumnado', `${sDocs} <small>de ${sPeople} ${sPeople === 1 ? 'persona' : 'personas'}</small>`, sNew ? `${sNew} sin abrir` : 'Todo visto')
        ].join('');
    },

    renderGroups() {
        const all = this.all();
        const counts = { todos: all.length, profesor: all.filter(d => !!d.professorId).length, alumno: all.filter(d => !!d.studentId).length };
        return [['todos', 'Todos'], ['profesor', 'Profesorado'], ['alumno', 'Alumnado']].map(([key, label]) =>
            `<button type="button" aria-pressed="${this.group === key}" class="${this.group === key ? 'is-on' : ''}" ${UIEvents.attr("click", function(event,captured){JustificantesView.setGroup(String(captured[0]));}, [key])}>${label} · ${counts[key]}</button>`).join('');
    },

    setGroup(group) {
        this.group = group;
        this.refreshInbox();
    },

    /** The inbox after the group, the search and «solo sin abrir»; the unopened ones first. */
    getJustificantes() {
        const all = this.all();
        const user = AuthManager.currentUser;
        if (!this.isReviewer()) {
            if (user?.professorId) return all.filter(j => j.professorId === user.professorId);
            if (user?.studentId) return all.filter(j => j.studentId === user.studentId);
            return [];
        }
        const term = this.search.trim().toLowerCase();
        return all.filter(j => this.group === 'todos' || (this.group === 'alumno' ? !!j.studentId : !!j.professorId))
            .filter(j => !this.onlyNew || !j.reviewedAt)
            .filter(j => !term || `${this.ownerName(j)} ${j.fileName}`.toLowerCase().includes(term));
    },

    attachEventListeners() {
        this.renderGrid();
    },

    /** Repaints the list (and, for whoever reviews, the figures, the filters and the chosen one). */
    renderGrid() {
        if (this.isReviewer()) this.refreshInbox();
        else this.renderMine();
    },

    refreshInbox() {
        const list = document.getElementById('justificantes-grid');
        if (!list) return;
        const stats = document.getElementById('jv-stats'), groups = document.getElementById('jv-groups');
        if (stats) SafeHTML.set(stats, this.renderStats());
        if (groups) SafeHTML.set(groups, this.renderGroups());
        const docs = this.getJustificantes().sort((a, b) => (!a.reviewedAt === !b.reviewedAt ? 0 : a.reviewedAt ? 1 : -1) || new Date(b.date) - new Date(a.date));
        const title = document.getElementById('jv-inbox-title');
        if (title) title.textContent = `Bandeja · ${docs.length}`;
        if (!docs.length) {
            const who = { todos: 'de nadie', profesor: 'del profesorado', alumno: 'del alumnado' }[this.group];
            SafeHTML.set(list, `<li class="jv-list-empty">${this.search || this.onlyNew ? 'Nada coincide con estos filtros.' : `No hay justificantes ${who}.`}</li>`);
        } else {
            SafeHTML.set(list, docs.map(doc => {
                const name = this.ownerName(doc), student = !!doc.studentId, on = doc.id === this.selectedId;
                return `<li><button type="button" class="jv-item ${on ? 'is-on' : ''}" aria-current="${on}" ${UIEvents.attr("click", function(event,captured){JustificantesView.select(String(captured[0]));}, [doc.id])}>
                    <span class="jv-avatar ${student ? 'is-student' : ''}" aria-hidden="true">${Utils.sanitizeHTML(this.initials(name))}</span>
                    <span class="jv-item-text">
                        <span class="jv-item-top"><strong>${Utils.sanitizeHTML(name)}</strong><span class="jv-item-when">${this.when(doc.date)}</span></span>
                        <span class="jv-item-file">${Utils.sanitizeHTML(doc.fileName)}</span>
                        <span class="jv-item-tags"><span class="jv-pill ${student ? 'is-student' : 'is-staff'}">${student ? 'Alumnado' : 'Profesorado'}</span>${doc.reviewedAt ? '<span class="jv-pill">Visto</span>' : '<span class="jv-pill is-new">Sin abrir</span>'}</span>
                    </span>
                </button></li>`;
            }).join(''));
        }
        this.renderDetail();
    },

    renderDetail() {
        const box = document.getElementById('jv-detail');
        if (!box) return;
        const doc = this.all().find(d => d.id === this.selectedId);
        if (!doc) {
            box.dataset.doc = '';
            SafeHTML.set(box, `<div class="jv-pick"><span class="jv-pick-icon">${this.ICONS.file}</span><strong>Elige un justificante de la bandeja</strong><span>Se ve aquí mismo. Al abrirlo queda como visto para todo el equipo.</span></div>`);
            return;
        }
        if (box.dataset.doc === doc.id) { this.renderFacts(doc); return; }
        box.dataset.doc = doc.id;
        const name = this.ownerName(doc), student = !!doc.studentId;
        SafeHTML.set(box, `
            <div class="jv-detail-head">
                <span class="jv-avatar is-big ${student ? 'is-student' : ''}" aria-hidden="true">${Utils.sanitizeHTML(this.initials(name))}</span>
                <span class="jv-detail-who">
                    <h2>${Utils.sanitizeHTML(name)}</h2>
                    <span id="jv-detail-count"></span>
                </span>
                <span class="jv-detail-actions">
                    <button type="button" class="jv-btn" ${UIEvents.attr("click", function(event,captured){JustificantesView.download(String(captured[0]));}, [doc.id])}>${this.ICONS.download} Descargar</button>
                    <button type="button" class="jv-btn" ${UIEvents.attr("click", function(event,captured){JustificantesView.preview(String(captured[0]));}, [doc.id])}>${this.ICONS.expand} Abrir en grande</button>
                    ${this.canDelete(doc) ? `<button type="button" class="jv-icon-btn is-danger" aria-label="Borrar ${Utils.sanitizeHTML(doc.fileName)}" title="Borrar" ${UIEvents.attr("click", function(event,captured){JustificantesView.remove(String(captured[0]));}, [doc.id])}>${this.ICONS.trash}</button>` : ''}
                </span>
            </div>
            <div class="jv-detail-body">
                <div class="jv-preview" id="jv-preview"><span class="jv-hint">Cargando el documento…</span></div>
                <aside class="jv-facts" id="jv-facts"></aside>
            </div>`);
        this.renderFacts(doc);
        const preview = document.getElementById('jv-preview');
        PrivateDownloads.renderStoredPreview(preview, doc.data, doc.type, 'justificantes.json', doc.id)
            .then(() => this.markReviewed(doc))
            .catch(error => { if (preview.isConnected) SafeHTML.set(preview, `<span class="jv-hint">${Utils.sanitizeHTML(error.message || 'Vista previa no disponible')}. Prueba a descargarlo.</span>`); });
    },

    renderFacts(doc) {
        const facts = document.getElementById('jv-facts');
        if (!facts) return;
        const mine = this.all().filter(d => (doc.studentId ? d.studentId === doc.studentId : d.professorId === doc.professorId))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
        const count = document.getElementById('jv-detail-count');
        if (count) count.textContent = `${doc.studentId ? 'Alumnado' : 'Profesorado'} · ${mine.length} ${mine.length === 1 ? 'justificante' : 'justificantes'}`;
        const others = mine.filter(d => d.id !== doc.id);
        const time = new Date(doc.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        SafeHTML.set(facts, `
            <div class="jv-fact"><span class="jv-mono">Archivo</span><strong class="jv-break">${Utils.sanitizeHTML(doc.fileName)}</strong><span class="jv-hint">${this.isPdf(doc) ? 'PDF' : 'Imagen'}</span></div>
            <div class="jv-fact"><span class="jv-mono">Subido</span><span>${this.day(doc.date)}, ${time}</span></div>
            <div class="jv-fact"><span class="jv-mono">Observaciones</span><span class="jv-notes">${doc.notes ? Utils.sanitizeHTML(doc.notes) : 'Sin observaciones.'}</span></div>
            <div class="jv-fact"><span class="jv-mono">Estado</span>${doc.reviewedAt ? '<span class="jv-pill is-seen">Visto</span>' : '<span class="jv-pill is-new">Sin abrir</span>'}<span class="jv-hint">Al abrirlo aquí queda como visto para todo el equipo.</span></div>
            <div class="jv-fact jv-others"><span class="jv-mono">Otros suyos</span>${others.length ? others.map(d => `<button type="button" class="jv-other" ${UIEvents.attr("click", function(event,captured){JustificantesView.select(String(captured[0]));}, [d.id])}><span>${Utils.sanitizeHTML(d.fileName)}</span><span class="jv-item-when">${this.when(d.date)}</span></button>`).join('') : '<span class="jv-hint">Ninguno más.</span>'}</div>`);
    },

    select(id) {
        if (this.selectedId === id) return;
        PrivateDownloads.clearInlinePreview(document.getElementById('jv-preview'));
        this.selectedId = id;
        this.refreshInbox();
    },

    // ---------- Profesor o alumno: enviar desde la página y ver los suyos ----------

    renderUploader() {
        const user = AuthManager.currentUser;
        return `
            <div class="jv-head">
                <span class="jv-mono">Mi espacio</span>
                <h1 class="jv-title">Mis justificantes</h1>
                <span class="jv-lead">Sube el justificante de una falta por motivos médicos. Lo revisan Coordinación y RRHH.</span>
            </div>
            <div class="jv-layout is-mine">
                <section class="jv-card jv-send" aria-labelledby="jv-send-title">
                    <h2 class="jv-h2" id="jv-send-title">Enviar un justificante</h2>
                    <input type="file" id="justificante-file" class="sr-only" accept="application/pdf,image/png,image/jpeg,image/gif,image/webp" ${UIEvents.attr("change", function(event,captured){JustificantesView.handleFileSelect(this);}, [])}>
                    <label for="justificante-file" class="jv-drop" id="file-drop-zone"
                        ${UIEvents.attr("dragover", function(event,captured){event.preventDefault();this.classList.add('is-over');}, [])}
                        ${UIEvents.attr("dragleave", function(event,captured){this.classList.remove('is-over');}, [])}
                        ${UIEvents.attr("drop", function(event,captured){event.preventDefault();this.classList.remove('is-over');JustificantesView.dropFile(event);}, [])}>
                        <span class="jv-drop-icon">${this.ICONS.upload}</span>
                        <strong>Arrastra el archivo o pulsa para elegirlo</strong>
                        <span>PDF, PNG o JPG · hasta 5 MB · también una foto con el móvil</span>
                    </label>
                    <div class="jv-chosen" id="file-info" hidden>
                        <span class="jv-chosen-icon">${this.ICONS.file}</span>
                        <span class="jv-chosen-text"><strong id="selected-file-name"></strong><span id="selected-file-kind"></span></span>
                        <button type="button" class="jv-btn is-small" ${UIEvents.attr("click", function(event,captured){JustificantesView.resetFileInput();}, [])}>Cambiar</button>
                    </div>
                    <label class="jv-field">Observaciones
                        <textarea id="justificante-notes" rows="4" placeholder="Qué días faltaste, a qué clases y el motivo, si quieres contarlo."></textarea>
                    </label>
                    <button type="button" id="submit-justificante-btn" class="jv-send-btn" disabled ${UIEvents.attr("click", function(event,captured){JustificantesView.handleUpload();}, [])}>${this.ICONS.send} Enviar justificante</button>
                    <span class="jv-hint">Aquí verás cuándo lo han visto. Si te equivocas de archivo, puedes borrarlo.</span>
                </section>
                <section class="jv-card jv-mine" aria-labelledby="jv-mine-title">
                    <div class="jv-card-head">
                        <h2 class="jv-h2" id="jv-mine-title">Enviados</h2>
                        <span class="jv-hint" id="jv-mine-sub"></span>
                    </div>
                    <ul class="jv-list" id="justificantes-grid"></ul>
                </section>
            </div>`;
    },

    renderMine() {
        const list = document.getElementById('justificantes-grid');
        if (!list) return;
        const docs = this.getJustificantes().sort((a, b) => new Date(b.date) - new Date(a.date));
        const seen = docs.filter(d => d.reviewedAt).length;
        const title = document.getElementById('jv-mine-title'), sub = document.getElementById('jv-mine-sub');
        if (title) title.textContent = `Enviados · ${docs.length}`;
        if (sub) sub.textContent = docs.length ? `${docs.length - seen} pendiente${docs.length - seen === 1 ? '' : 's'} de ver · ${seen} visto${seen === 1 ? '' : 's'}` : '';
        if (!docs.length) {
            SafeHTML.set(list, '<li class="jv-list-empty">Todavía no has enviado ningún justificante.</li>');
            return;
        }
        SafeHTML.set(list, docs.map(doc => `<li class="jv-row">
            <span class="jv-ext ${this.isPdf(doc) ? 'is-pdf' : ''}" aria-hidden="true">${this.isPdf(doc) ? 'PDF' : 'IMG'}</span>
            <span class="jv-row-text">
                <span class="jv-row-top"><strong>${Utils.sanitizeHTML(doc.fileName)}</strong>${doc.reviewedAt ? '<span class="jv-pill is-seen">Visto</span>' : '<span class="jv-pill is-wait">Pendiente de ver</span>'}</span>
                <span class="jv-hint">Enviado el ${this.day(doc.date)}</span>
                ${doc.notes ? `<span class="jv-row-notes">${Utils.sanitizeHTML(doc.notes)}</span>` : ''}
            </span>
            <span class="jv-row-actions">
                <button type="button" class="jv-icon-btn" aria-label="Ver ${Utils.sanitizeHTML(doc.fileName)}" title="Ver" ${UIEvents.attr("click", function(event,captured){JustificantesView.preview(String(captured[0]));}, [doc.id])}>${this.ICONS.eye}</button>
                <button type="button" class="jv-icon-btn" aria-label="Descargar ${Utils.sanitizeHTML(doc.fileName)}" title="Descargar" ${UIEvents.attr("click", function(event,captured){JustificantesView.download(String(captured[0]));}, [doc.id])}>${this.ICONS.download}</button>
                ${this.canDelete(doc) ? `<button type="button" class="jv-icon-btn is-danger" aria-label="Borrar ${Utils.sanitizeHTML(doc.fileName)}" title="Borrar" ${UIEvents.attr("click", function(event,captured){JustificantesView.remove(String(captured[0]));}, [doc.id])}>${this.ICONS.trash}</button>` : ''}
            </span>
        </li>`).join(''));
    },

    storeLocal(all) {
        FileStorage.dataStore.justificantes = all;
        BrowserState.setItem(DataManager.KEYS.JUSTIFICANTES, JSON.stringify(all));
    },

    // El servidor marca como visto al abrir el archivo quien revisa; aquí se refleja sin recargar la página.
    markReviewed(doc) {
        if (!this.isReviewer() || doc.reviewedAt || String(doc.data || '').startsWith('data:')) return;
        const all = this.all();
        const stored = all.find(item => item.id === doc.id);
        if (!stored || stored.reviewedAt) return;
        stored.reviewedAt = new Date().toISOString();
        this.storeLocal(all);
        this.renderGrid();
    },

    remove(id) {
        const doc = this.all().find(item => item.id === id);
        if (!doc || !this.canDelete(doc) || this.deleting.has(id)) return;
        Utils.showConfirm(`¿Borrar el justificante «${doc.fileName}»? Se eliminará también el archivo y no se puede deshacer.`, async () => {
            this.deleting.add(id);
            try {
                const response = await fetch('/api/documents/file/justificantes.json/' + encodeURIComponent(id), {
                    method: 'DELETE', headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() || '' }, cache: 'no-store'
                });
                const body = await response.json().catch(() => ({}));
                if (!response.ok || !body.success) throw new Error(body.error || 'No se pudo borrar el justificante');
                this.storeLocal(this.all().filter(item => item.id !== id));
                if (this.selectedId === id) {
                    PrivateDownloads.clearInlinePreview(document.getElementById('jv-preview'));
                    this.selectedId = null;
                }
                Utils.showToast('Justificante borrado', 'success');
                this.renderGrid();
            } catch (error) {
                Utils.showToast(error.message, 'error');
            } finally {
                this.deleting.delete(id);
            }
        });
    },

    handleFileSelect(input) {
        const file = input.files[0];
        if (!file) return this.resetFileInput();
        const kind = file.type.includes('pdf') ? 'PDF' : 'Imagen';
        const size = file.size >= 1048576 ? `${(file.size / 1048576).toLocaleString('es-ES', { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`;
        document.getElementById('selected-file-name').textContent = file.name;
        document.getElementById('selected-file-kind').textContent = `${kind} · ${size}`;
        document.getElementById('file-drop-zone').hidden = true;
        document.getElementById('file-info').hidden = false;
        document.getElementById('submit-justificante-btn').disabled = false;
    },

    // A file dropped on the zone goes into the same input as one picked by hand.
    dropFile(event) {
        const input = document.getElementById('justificante-file');
        const file = event.dataTransfer?.files?.[0];
        if (!input || !file) return;
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
        this.handleFileSelect(input);
    },

    resetFileInput(keepNotes = true) {
        const input = document.getElementById('justificante-file');
        if (input) input.value = '';
        if (!keepNotes) { const notes = document.getElementById('justificante-notes'); if (notes) notes.value = ''; }
        const zone = document.getElementById('file-drop-zone'), info = document.getElementById('file-info'), btn = document.getElementById('submit-justificante-btn');
        if (zone) zone.hidden = false;
        if (info) info.hidden = true;
        if (btn) btn.disabled = true;
    },

    async handleUpload() {
        if(!this.canUpload())return;
        if (this._uploadPending) return;
        const fileInput = document.getElementById('justificante-file');
        const notesInput = document.getElementById('justificante-notes');
        const file = fileInput ? fileInput.files[0] : null;

        if (!file) {
            Utils.showToast('Por favor, selecciona un archivo', 'warning');
            return;
        }

        this._uploadPending = true;
        const btn = document.getElementById('submit-justificante-btn');
        if (btn) {
            btn.disabled = true;
            SafeHTML.set(btn, '<i class="fas fa-spinner fa-spin"></i> Subiendo…');
            btn.setAttribute('aria-busy','true');
        }

        let sent = false;
        try {
            const base64Data = await this.fileToBase64(file);
            const currentUser = AuthManager.currentUser;
            const isStudent = !currentUser.professorId && !!currentUser.studentId;
            const docId = 'just_' + Date.now();

            const newDoc = {
                id: docId,
                professorId: isStudent ? null : (currentUser.professorId || null),
                professorName: isStudent ? null : currentUser.name,
                studentId: isStudent ? currentUser.studentId : null,
                studentName: isStudent ? currentUser.name : null,
                fileName: file.name,
                type: file.type,
                date: new Date().toISOString(),
                notes: notesInput ? notesInput.value : '',
                data: base64Data
            };

            const all = [...this.all()];
            all.push(newDoc);
            const globalWrite=AuthManager.canManageModule('justificantes',['admin','rrhh']);
            const own=all.filter(d=>isStudent ? d.studentId===currentUser.studentId && !d.professorId : d.professorId===currentUser.professorId && !d.studentId);
            await FileStorage.writeConfirmed(DataManager.KEYS.JUSTIFICANTES, globalWrite?all:own);
            this.storeLocal(all);

            this.notifyReviewers(newDoc);
            Utils.showToast('Justificante enviado con éxito', 'success');
            sent = true;
        } catch (error) {
            console.error('Upload error:', error);
            Utils.showToast(error.message || 'No se pudo guardar el archivo.', 'error');
        } finally {
            this._uploadPending = false;
            const finalBtn = document.getElementById('submit-justificante-btn');
            if (finalBtn) {
                finalBtn.removeAttribute('aria-busy');
                SafeHTML.set(finalBtn, `${this.ICONS.send} Enviar justificante`);
                finalBtn.disabled = !document.getElementById('justificante-file')?.files?.length;
            }
            if (sent) { this.resetFileInput(false); this.renderGrid(); }
        }
    },

    fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    },

    notifyReviewers(doc) {
        const isStudentDoc = !!doc.studentId;
        const ownerName = doc.professorName || doc.studentName;
        const ownerLabel = isStudentDoc ? 'alumno/a' : 'profesor/a';
        const senderId = doc.professorId || doc.studentId;

        // Notifica solo a Admin y RRHH — Coordinación Académica ya no recibe
        // justificantes médicos, no es de su competencia.
        const reviewerUsers = AuthManager.users.filter(u => u.role === 'rrhh' || u.role === 'admin');

        if (reviewerUsers.length > 0) {
            reviewerUsers.forEach(reviewer => {
                DataManager.addNotification({
                    title: 'Nuevo Justificante Médico',
                    message: `El ${ownerLabel} ${ownerName} ha subido un justificante: ${doc.fileName}`,
                    type: 'info',
                    senderId: senderId,
                    senderName: ownerName,
                    receiverId: reviewer.username,
                    targetView: 'justificantes'
                });
            });
            Utils.showToast('Notificación enviada al equipo revisor', 'info');
        } else {
            // Fallback for default RRHH ID
            DataManager.addNotification({
                title: 'Nuevo Justificante Médico',
                message: `El ${ownerLabel} ${ownerName} ha subido un justificante: ${doc.fileName}`,
                type: 'info',
                senderId: senderId,
                senderName: ownerName,
                receiverId: 'rrhh_1',
                targetView: 'justificantes'
            });
        }
    },

    async preview(id) {
        const doc = this.all().find(d => d.id === id);
        if (!doc) return;
        const modal = document.getElementById('preview-justificante-modal');
        const content = document.getElementById('preview-content');
        const title = document.getElementById('preview-title');
        if (title) title.textContent = doc.fileName;
        if (content) {
            try { await PrivateDownloads.renderStoredPreview(content, doc.data, doc.type, 'justificantes.json', doc.id); }
            catch (error) { Utils.showToast(error.message, 'error'); return; }
            this.markReviewedLater = doc;
        }
        if (modal) {
            modal.style.display = 'flex';
            setTimeout(() => { modal.style.opacity = '1'; }, 10);
        }
    },

    closePreview() {
        PrivateDownloads.clearInlinePreview(document.getElementById('preview-content'));
        const reviewed = this.markReviewedLater;
        this.markReviewedLater = null;
        if (reviewed) this.markReviewed(reviewed);
        const modal = document.getElementById('preview-justificante-modal');
        if (!modal) return;
        modal.style.opacity = '0';
        setTimeout(() => { modal.style.display = 'none'; }, 250);
    },

    download(id) {
        const doc = this.all().find(d => d.id === id);
        if (!doc) return;

        if (!String(doc.data || '').startsWith('data:')) {
            PrivateDownloads.download(PrivateDownloads.storedUrl('justificantes.json', doc.id)).then(ok => { if (ok) this.markReviewed(doc); });
            return;
        }
        const link = document.createElement('a');
        link.href = doc.data;
        link.download = doc.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },

    styles() {
        return `<style ${SafeHTML.styleAttr}>
            .jv { gap: 1.1rem; padding-top: 1rem; }
            .jv [hidden] { display: none !important; }
            .jv .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
            .jv-head { display: flex; flex-direction: column; gap: 0.15rem; }
            .jv-mono { font-family: var(--dnr-mono); font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--dnr-ink-mute); }
            .jv-title { margin: 0; font-family: var(--dnr-display); font-weight: 400; font-size: 2.75rem; letter-spacing: 0.02em; line-height: 1; color: var(--dnr-ink); }
            .jv-lead { font-size: 0.85rem; color: var(--dnr-ink-mute); }
            .jv-hint { font-size: 0.76rem; color: var(--dnr-ink-mute); line-height: 1.4; }
            .jv-card { background: var(--dnr-surface); border: 1px solid var(--dnr-line); border-radius: 16px; }
            .jv-card-head { display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; flex-wrap: wrap; padding: 0.85rem 1.1rem; border-bottom: 1px solid var(--dnr-line); }
            .jv-h2 { margin: 0; font-family: var(--dnr-display); font-weight: 400; font-size: 1.6rem; letter-spacing: 0.02em; line-height: 1; color: var(--dnr-ink); }
            .jv-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.75rem; }
            .jv-stat { background: var(--dnr-surface); border: 1px solid var(--dnr-line); border-radius: 14px; padding: 0.85rem 1rem; display: flex; flex-direction: column; gap: 0.25rem; }
            .jv-stat.is-warn { border-color: var(--dnr-amber-500); }
            .jv-stat-label { font-family: var(--dnr-mono); font-size: 0.66rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: var(--dnr-ink-mute); }
            .jv-stat-value { font-family: var(--dnr-display); font-size: 2.1rem; line-height: 1; color: var(--dnr-ink); }
            .jv-stat-value small { font-size: 1.2rem; color: var(--dnr-ink-mute); }
            .jv-stat-sub { font-size: 0.75rem; color: var(--dnr-ink-mute); }
            .jv-stat.is-warn .jv-stat-sub { color: #7A4A12; font-weight: 600; }
            .jv-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; padding: 0.75rem 1rem; }
            .jv-search { display: flex; align-items: center; gap: 0.5rem; flex: 1 1 220px; max-width: 320px; border: 1px solid var(--dnr-line-strong, #C4D0CA); border-radius: 10px; padding: 0.5rem 0.75rem; color: var(--dnr-ink-mute); }
            .jv-search input { border: 0; outline: 0; background: transparent; width: 100%; font: inherit; font-size: 0.88rem; color: var(--dnr-ink); }
            .jv-segment { display: inline-flex; border: 1px solid var(--dnr-line-strong, #C4D0CA); border-radius: 10px; overflow: hidden; }
            .jv-segment button { font: inherit; font-size: 0.82rem; font-weight: 600; padding: 0.55rem 0.9rem; border: 0; background: var(--dnr-surface); color: var(--dnr-ink-mute); cursor: pointer; }
            .jv-segment button + button { border-left: 1px solid var(--dnr-line-strong, #C4D0CA); }
            .jv-segment button.is-on { background: var(--dnr-teal-700); color: #fff; }
            .jv-check { display: inline-flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; color: var(--dnr-ink-mute); }
            .jv-check input { width: 16px; height: 16px; accent-color: var(--dnr-teal-700); }
            .jv-layout { display: flex; flex-wrap: wrap; gap: 1.1rem; align-items: stretch; }
            .jv-inbox { flex: 1 1 340px; max-width: 440px; display: flex; flex-direction: column; overflow: hidden; box-shadow: var(--dnr-shadow-card); }
            .jv-list { list-style: none; margin: 0; padding: 0; overflow-y: auto; max-height: min(68vh, 760px); }
            .jv-list-empty { padding: 2rem 1.1rem; text-align: center; color: var(--dnr-ink-mute); font-size: 0.88rem; }
            .jv-item { width: 100%; display: flex; gap: 0.75rem; align-items: flex-start; text-align: left; padding: 0.75rem 1.1rem; border: 0; border-top: 1px solid var(--dnr-surface-sunken); background: var(--dnr-surface); color: var(--dnr-ink); font: inherit; cursor: pointer; }
            .jv-list li:first-child .jv-item { border-top: 0; }
            .jv-item:hover { background: #F7FAF9; }
            .jv-item.is-on { background: #E8F4F1; box-shadow: inset 3px 0 0 var(--dnr-teal-700); }
            .jv-item:focus-visible { outline: 2px solid var(--dnr-teal-600); outline-offset: -2px; }
            .jv-avatar { width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; font-family: var(--dnr-display); font-size: 0.95rem; letter-spacing: 0.04em; background: var(--dnr-teal-900); color: #fff; }
            .jv-avatar.is-student { background: var(--dnr-amber-100); color: #7A4A12; }
            .jv-avatar.is-big { width: 46px; height: 46px; font-size: 1.15rem; }
            .jv-item-text { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; flex: 1; }
            .jv-item-top { display: flex; justify-content: space-between; gap: 0.5rem; align-items: baseline; }
            .jv-item-top strong { font-size: 0.88rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .jv-item-when { font-family: var(--dnr-mono); font-size: 0.72rem; color: var(--dnr-ink-mute); white-space: nowrap; }
            .jv-item-file { font-size: 0.8rem; color: var(--dnr-ink); opacity: 0.85; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .jv-item-tags { display: flex; gap: 0.35rem; margin-top: 0.15rem; }
            .jv-pill { display: inline-flex; align-self: flex-start; padding: 0.12rem 0.5rem; border-radius: 999px; font-family: var(--dnr-mono); font-size: 0.62rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap; background: var(--dnr-surface-sunken); color: var(--dnr-ink-mute); border: 1px solid var(--dnr-line); }
            .jv-pill.is-staff { background: #E0F2EF; color: var(--dnr-teal-700); border-color: #A9D9CF; }
            .jv-pill.is-student { background: var(--dnr-amber-100); color: #7A4A12; border-color: #F0C995; }
            .jv-pill.is-new { background: var(--dnr-surface); color: #B33A2E; border-color: #E8A39B; }
            .jv-pill.is-seen { background: #E4F5EC; color: #14663F; border-color: #BFE6D0; }
            .jv-pill.is-wait { background: var(--dnr-amber-100); color: #7A4A12; border-color: #F0C995; }
            .jv-detail { flex: 999 1 520px; min-width: 0; display: flex; flex-direction: column; overflow: hidden; min-height: 520px; }
            .jv-pick { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem; padding: 3rem 1.5rem; text-align: center; color: var(--dnr-ink-mute); font-size: 0.85rem; }
            .jv-pick strong { color: var(--dnr-ink); font-size: 1rem; }
            .jv-pick-icon { width: 52px; height: 52px; border-radius: 14px; background: var(--dnr-surface-sunken); color: var(--dnr-teal-600); display: inline-flex; align-items: center; justify-content: center; }
            .jv-detail-head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem; padding: 0.85rem 1.1rem; border-bottom: 1px solid var(--dnr-line); }
            .jv-detail-who { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; flex: 1; }
            .jv-detail-who h2 { margin: 0; font-size: 1.05rem; font-weight: 700; color: var(--dnr-ink); }
            .jv-detail-who span { font-size: 0.8rem; color: var(--dnr-ink-mute); }
            .jv-detail-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
            .jv-btn { display: inline-flex; align-items: center; gap: 0.45rem; font: inherit; font-size: 0.84rem; font-weight: 600; padding: 0.55rem 0.8rem; border: 1px solid var(--dnr-line-strong, #C4D0CA); border-radius: 10px; background: var(--dnr-surface); color: var(--dnr-ink); cursor: pointer; }
            .jv-btn svg { color: #14867A; }
            .jv-btn:hover { border-color: var(--dnr-teal-500); }
            .jv-btn.is-small { font-size: 0.8rem; padding: 0.4rem 0.7rem; }
            .jv-icon-btn { width: 38px; height: 38px; border: 1px solid var(--dnr-line); border-radius: 10px; background: var(--dnr-surface); color: var(--dnr-ink-mute); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
            .jv-icon-btn:hover { border-color: var(--dnr-teal-500); color: var(--dnr-teal-700); }
            .jv-icon-btn.is-danger { border-color: #E8A39B; color: #B33A2E; }
            .jv-icon-btn.is-danger:hover { background: #FBE9E7; }
            .jv-btn:focus-visible, .jv-icon-btn:focus-visible, .jv-other:focus-visible, .jv-segment button:focus-visible { outline: 2px solid var(--dnr-teal-600); outline-offset: 2px; }
            .jv-detail-body { display: flex; flex-wrap: wrap; flex: 1; }
            .jv-preview { flex: 999 1 360px; min-width: 0; min-height: min(62vh, 680px); background: #E9EEEB; display: flex; align-items: center; justify-content: center; padding: 1rem; box-sizing: border-box; }
            .jv-preview iframe { width: 100%; height: min(62vh, 680px) !important; border-radius: 6px; box-shadow: 0 10px 30px -12px rgba(10,25,22,0.35); }
            .jv-preview img { max-height: min(60vh, 660px) !important; border-radius: 6px; box-shadow: 0 10px 30px -12px rgba(10,25,22,0.35); background: #fff; }
            .jv-facts { flex: 1 1 220px; max-width: 290px; padding: 1rem 1.1rem; display: flex; flex-direction: column; gap: 0.9rem; border-left: 1px solid var(--dnr-surface-sunken); font-size: 0.85rem; color: var(--dnr-ink); }
            .jv-fact { display: flex; flex-direction: column; gap: 0.3rem; }
            .jv-break { overflow-wrap: anywhere; }
            .jv-notes { line-height: 1.45; background: var(--dnr-surface-sunken); border-radius: 10px; padding: 0.6rem 0.75rem; white-space: pre-line; }
            .jv-others { padding-top: 0.8rem; border-top: 1px solid var(--dnr-surface-sunken); }
            .jv-other { display: flex; justify-content: space-between; gap: 0.5rem; font: inherit; font-size: 0.8rem; text-align: left; padding: 0.3rem 0; border: 0; background: none; color: var(--dnr-ink); cursor: pointer; }
            .jv-other span:first-child { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .jv-other:hover span:first-child { color: var(--dnr-teal-700); text-decoration: underline; }
            .jv-layout.is-mine { align-items: flex-start; }
            .jv-send { flex: 1 1 340px; max-width: 470px; padding: 1.2rem 1.3rem; display: flex; flex-direction: column; gap: 0.95rem; box-shadow: var(--dnr-shadow-card); }
            .jv-drop { border: 2px dashed #9FB3AA; border-radius: 14px; background: var(--dnr-surface-sunken); padding: 1.8rem 1.2rem; display: flex; flex-direction: column; align-items: center; gap: 0.45rem; text-align: center; cursor: pointer; color: var(--dnr-ink-mute); font-size: 0.78rem; }
            .jv-drop strong { color: var(--dnr-ink); font-size: 0.95rem; }
            .jv-drop.is-over, .jv-drop:hover { border-color: var(--dnr-teal-500); background: #E8F4F1; }
            #justificante-file:focus-visible + .jv-drop { outline: 2px solid var(--dnr-teal-600); outline-offset: 2px; }
            .jv-drop-icon { width: 52px; height: 52px; border-radius: 14px; background: var(--dnr-surface); color: #14867A; display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px -4px rgba(10,25,22,0.2); }
            .jv-chosen { display: flex; align-items: center; gap: 0.75rem; border: 1px solid #A9D9CF; background: #E8F4F1; border-radius: 14px; padding: 0.85rem 1rem; }
            .jv-chosen-icon { width: 42px; height: 42px; border-radius: 10px; background: var(--dnr-surface); color: var(--dnr-teal-700); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
            .jv-chosen-text { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; flex: 1; font-size: 0.76rem; color: var(--dnr-ink-mute); }
            .jv-chosen-text strong { font-size: 0.88rem; color: var(--dnr-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .jv-field { display: flex; flex-direction: column; gap: 0.35rem; font-size: 0.78rem; font-weight: 600; color: var(--dnr-ink); }
            .jv-field textarea { padding: 0.65rem 0.75rem; border: 1px solid var(--dnr-line-strong, #C4D0CA); border-radius: 10px; font: inherit; font-size: 0.88rem; font-weight: 400; color: var(--dnr-ink); background: var(--dnr-surface); resize: vertical; }
            .jv-field textarea:focus { outline: 0; border-color: var(--dnr-teal-500); box-shadow: 0 0 0 3px var(--dnr-teal-100); }
            .jv-send-btn { display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; font: inherit; font-size: 0.95rem; font-weight: 600; padding: 0.8rem 1.1rem; border: 0; border-radius: 12px; background: var(--dnr-teal-700); color: #fff; cursor: pointer; }
            .jv-send-btn:hover:not(:disabled) { background: var(--dnr-teal-900); }
            .jv-send-btn:disabled { opacity: 0.45; cursor: not-allowed; }
            .jv-mine { flex: 999 1 480px; min-width: 0; overflow: hidden; }
            .jv-row { display: grid; grid-template-columns: 44px minmax(0, 1fr) auto; gap: 0.85rem; align-items: center; padding: 0.85rem 1.1rem; border-top: 1px solid var(--dnr-surface-sunken); }
            .jv-row:first-child { border-top: 0; }
            .jv-ext { width: 44px; height: 44px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-family: var(--dnr-mono); font-size: 0.68rem; font-weight: 700; background: #E0F2EF; color: var(--dnr-teal-700); }
            .jv-ext.is-pdf { background: #FDECEA; color: #B33A2E; }
            .jv-row-text { display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; }
            .jv-row-top { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
            .jv-row-top strong { font-size: 0.88rem; overflow-wrap: anywhere; }
            .jv-row-notes { font-size: 0.8rem; color: var(--dnr-ink); opacity: 0.85; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .jv-row-actions { display: flex; gap: 0.35rem; }
            .jv-empty { padding: 5rem 1rem; text-align: center; }
            .jv-modal { position: fixed; inset: 0; background: rgba(8,49,45,0.75); z-index: 9999; align-items: center; justify-content: center; transition: opacity 0.25s ease; }
            .jv-modal-box { gap: 0; width: 90%; max-width: 900px; height: 90%; background: var(--dnr-surface); border-radius: 16px; display: flex; flex-direction: column; overflow: hidden; }
            .jv-modal-head { padding: 0.9rem 1.2rem; border-bottom: 1px solid var(--dnr-line); display: flex; justify-content: space-between; align-items: center; gap: 1rem; }
            .jv-modal-head h3 { margin: 0; font-size: 1rem; color: var(--dnr-ink); overflow-wrap: anywhere; }
            .jv-modal-body { flex: 1; display: flex; align-items: center; justify-content: center; background: var(--dnr-surface-sunken); overflow: auto; }
            @media (max-width: 900px) { .jv-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } .jv-inbox, .jv-send { max-width: none; } .jv-facts { max-width: none; border-left: 0; border-top: 1px solid var(--dnr-surface-sunken); } }
            @media (max-width: 520px) { .jv-stats { grid-template-columns: 1fr; } .jv-row { grid-template-columns: 1fr; } }
        </style>`;
    }
};

window.JustificantesView = JustificantesView;
